/**
 * MCS Panel
 *
 * Main panel for the Medical Coding Specification document.
 * Displays sections grouped by category and a coding-domains table showing
 * which coding domains (MedDRA, WHODrug, LOINC) were detected in the study.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useState, useEffect } from 'react';
import SafeHtml from '../common/SafeHtml';
import { SectionStatusBadge, SectionFailureNotice } from '../common/SectionStatus';
import { sortSections, compareSectionNumbers } from '../../features/documents/sectionOrder';
import { regenerateIconClass } from '../../features/documents/sectionStatus';
import { generationStatusNotice } from '../../features/documents/edtsDisplay';
import {
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc, dataManagementSubcollection } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import { studyDocumentDownloadOptions } from '../../features/downloads/studyDocumentDownloads';
import {
  Loader2,
  Play,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  BookOpen,
  AlertTriangle,
  CheckCircle2,
  Hash,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

interface DetectedCodingDomain {
  type: string;
  displayName: string;
  dictionary: string;
  sdtmDomain: string;
  verbatimVariable: string;
  /** The SDTM target variable the coded term goes to (AEDECOD, LBLOINC). */
  decodedVariable: string;
  evidence: string[];
  crfFormNames: string[];
  fieldCount: number;
  /**
   * False when the study collects no verbatim term for this domain
   * (forms in the domain capture structured metadata only). When false,
   * verbatimVariable will be the empty string.
   */
  verbatimVariablePresent?: boolean;
}

interface MCSDoc {
  version: number;
  status: string;
  title: string;
  generatedAt: any;
  generatedBy: string;
  studyInfo?: any;
  detectedDomains?: DetectedCodingDomain[];
  domainCount?: number;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
  failedSectionCount?: number;
  generationStatus?: string;
}

interface MCSSection {
  id: string;
  sectionId: string;
  number: string;
  title: string;
  group: string;
  content: string;
  status: string;
  placeholderCount?: number;
  placeholders?: string[];
}

interface MCSPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's medicalCodingSpec card, which
   * carries its own Regenerate button, so two controls for the same action sit
   * on screen together. Each used to hold only its own state. Both do read the
   * server's `_generating` marker, but that is set only after a round trip, so
   * at the instant of a click neither could see the other's — and on
   * 2026-08-17 one click produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs` ORed
   * with the same marker), so passing it down closes that window immediately
   * rather than a round trip later.
   */
  generating?: boolean;
}

/* ---------- Constants ---------- */

/**
 * Unlike the other document panels, each MCS section row has its own
 * regenerate icon (title "Regenerate this section"), as well as the panel's
 * Regenerate All Sections button, so the hint names both. The icon is
 * revealed on hover, but always shown on a failed section, so the hint never
 * points to something the user cannot see (regenerateIconClass).
 */
const MCS_REGENERATE_HINT = 'Use the regenerate icon on this section, or Regenerate All Sections, to try it again.';

const GROUP_META: Record<string, { label: string; bg: string; border: string; text: string }> = {
  introduction:       { label: 'Introduction',                   bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700' },
  dictionary_specs:   { label: 'Dictionary Specifications',      bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700' },
  coding_conventions: { label: 'Coding Conventions',             bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700' },
  auto_coding:        { label: 'Auto-Coding',                    bg: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700' },
  manual_coding:      { label: 'Manual Coding',                  bg: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700' },
  governance:         { label: 'Organization & Governance',      bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700' },
  quality_control:    { label: 'Quality Control',                bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  escalation:         { label: 'Escalation & Adjudication',      bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
  systems:            { label: 'System & Technical Requirements', bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700' },
  training:           { label: 'Training',                       bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700' },
  domain_specs:       { label: 'Domain-Specific Coding',         bg: 'bg-lime-50',    border: 'border-lime-200',    text: 'text-lime-700' },
  dictionary_updates: { label: 'Dictionary Updates',             bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700' },
  appendices:         { label: 'Appendices',                     bg: 'bg-stone-50',   border: 'border-stone-200',   text: 'text-stone-700' },
};

const STATUS_STYLES: Record<string, string> = {
  draft:       'bg-gray-100 text-gray-600',
  'in-review': 'bg-blue-100 text-blue-700',
  in_review:   'bg-blue-100 text-blue-700',
  approved:    'bg-green-100 text-green-700',
  generated:   'bg-blue-100 text-blue-700',
  edited:      'bg-amber-100 text-amber-700',
  reviewed:    'bg-green-100 text-green-700',
};

const PROGRESS_STEPS = [
  'Loading protocol and study setup...',
  'Scanning CRFs for coding domains...',
  'Detecting dictionary requirements...',
  'Generating dictionary specifications...',
  'Generating coding conventions...',
  'Generating per-domain coding specifications...',
  'Generating quality control sections...',
  'Generating appendices...',
  'Finalizing document...',
];

/* ---------- Helpers ---------- */

function groupSections(sections: MCSSection[]): Record<string, MCSSection[]> {
  const groups: Record<string, MCSSection[]> = {};
  for (const s of sections) {
    const key = (s.group || 'other').toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  }
  // Sort within each group by section number
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => compareSectionNumbers(a.number, b.number));
  }
  return groups;
}

function getGroupStyle(group: string) {
  const key = group.toLowerCase();
  return GROUP_META[key] || { label: group, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };
}

function formatGroupLabel(group: string): string {
  const meta = GROUP_META[group.toLowerCase()];
  if (meta) return meta.label;
  return group
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/* ---------- Component ---------- */

export default function MCSPanel({ projectId, generating: hubGenerating = false }: MCSPanelProps) {
  const [mcsDoc, setMcsDoc] = useState<MCSDoc | null>(null);
  const [sections, setSections] = useState<MCSSection[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'sections' | 'domains'>('sections');
  const [loading, setLoading] = useState(true);
  // Shares its running state with the card's Download button (same group).
  const download = useDocumentDownload(projectId, 'medicalCodingSpec');
  const [regeneratingSection, setRegeneratingSection] = useState<string | null>(null);
  const { showToast } = useToast();

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS; // the lock window; shared with the backend

  // Detect in-flight generation from the Firestore marker (cross-session/cross-tab aware)
  const isGeneratingFromMarker = (() => {
    if (!mcsDoc?._generating) return false;
    const startedAt = mcsDoc._generationStartedAt;
    if (!startedAt?.toDate) return true; // No timestamp yet — assume fresh
    const startedMs = startedAt.toDate().getTime();
    const ageMs = Date.now() - startedMs;
    return ageMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!mcsDoc?._generating) return false;
    const startedAt = mcsDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    const startedMs = startedAt.toDate().getTime();
    return Date.now() - startedMs >= STALE_GENERATION_MS;
  })();

  // Combine local 'generating' state (in-tab fast path), the Hub's in-flight
  // state (the shared guard — see MCSPanelProps.generating) and the Firestore
  // marker. The Hub's is what makes a click on ITS Regenerate button disable
  // this one synchronously, instead of a server round trip later.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  /* --- Firestore: MCS metadata --- */
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'medicalCodingSpec'),
      (snapshot) => {
        if (snapshot.exists()) {
          setMcsDoc(snapshot.data() as MCSDoc);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading MCS:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  /* --- Firestore: sections --- */
  useEffect(() => {
    if (!projectId || !mcsDoc) return;

    const sectionsRef = dataManagementSubcollection(projectId, 'medicalCodingSpec', 'sections');

    const unsubscribe = onSnapshot(
      query(sectionsRef, orderBy('number')),
      (snapshot) => {
        const list: MCSSection[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<MCSSection, 'id'>),
        }));
        setSections(sortSections(list));
      },
      (err) => {
        console.error('Error loading MCS sections:', err);
      }
    );

    return () => unsubscribe();
  }, [projectId, mcsDoc]);

  /* --- Generate MCS --- */
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);

    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 15000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_MCS_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('MCS generation error:', err);
      showToast(err.message || 'Failed to generate Medical Coding Specification', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  /* --- Regenerate a single section --- */
  const handleRegenerateSection = async (sectionId: string) => {
    setRegeneratingSection(sectionId);
    try {
      const regenFn = httpsCallable(functions, CLOUD_FUNCTIONS.REGENERATE_MCS_SECTION, { timeout: CALLABLE_TIMEOUTS.LONG });
      await regenFn({ projectId, sectionId });
    } catch (err: any) {
      console.error('MCS section regeneration error:', err);
      showToast(err.message || 'Failed to regenerate section', 'error');
    } finally {
      setRegeneratingSection(null);
    }
  };

  /* --- Derived data --- */
  const grouped = groupSections(sections);
  const detectedDomains: DetectedCodingDomain[] = mcsDoc?.detectedDomains || [];
  // Stored as incomplete when any section failed (docgen reasons, 2026-10-01).
  const incompleteNotice = mcsDoc ? generationStatusNotice(mcsDoc) : undefined;

  const statusLabel = mcsDoc?.status || 'draft';
  const statusStyle = STATUS_STYLES[statusLabel] || STATUS_STYLES.draft;

  const totalPlaceholders = sections.reduce(
    (sum, s) => sum + (s.placeholderCount || s.placeholders?.length || 0),
    0
  );

  /* --- Loading state --- */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  /* --- Not yet generated (or marker-only doc during first generation) --- */
  const hasRealContent = !!(mcsDoc && (mcsDoc.version || mcsDoc.title));
  if (!mcsDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate a Medical Coding Specification covering MedDRA, WHODrug Global, and LOINC
            coding conventions, dictionary versions, per-domain coding procedures, auto-coding
            thresholds, manual coding workflows, quality control, escalation paths, and training
            requirements.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol, study setup, CRFs, data dictionary, and edit checks.
            The platform detects coding domains from your CRF forms and generates per-domain subsections
            for verbatim-to-coded variable mappings, dictionary hierarchy paths, and QC procedures.
          </p>

          {mcsDoc?._generationError && !mcsDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(mcsDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {generating
                  ? 'Generating...'
                  : 'Generation in progress in the background...'}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 3-5 minutes as each section is generated individually.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Medical Coding Specification
            </button>
          )}
        </div>
      </div>
    );
  }

  /* --- MCS exists: full UI --- */
  return (
    <div className="space-y-3">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">Medical Coding Specification</h2>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
            {statusLabel === 'in_review' || statusLabel === 'in-review'
              ? 'In Review'
              : statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {sections.length} section{sections.length !== 1 ? 's' : ''}
            {mcsDoc?.domainCount != null && (
              <> &middot; {mcsDoc.domainCount} coding domain{mcsDoc.domainCount !== 1 ? 's' : ''}</>
            )}
            {totalPlaceholders > 0 && (
              <> &middot; {totalPlaceholders} placeholder{totalPlaceholders !== 1 ? 's' : ''}</>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Tab toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => setActiveTab('sections')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'sections'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="h-3.5 w-3.5" />
              Sections
            </button>
            <button
              onClick={() => setActiveTab('domains')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'domains'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Hash className="h-3.5 w-3.5" />
              Coding Domains
              {detectedDomains.length > 0 && (
                <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
                  {detectedDomains.length}
                </span>
              )}
            </button>
          </div>

          {/* Download */}
          <DownloadButton
            download={download}
            options={studyDocumentDownloadOptions('medicalCodingSpec', { projectId })}
            variant="secondary"
            size="sm"
          />
        </div>
      </div>

      {/* Previous generation error */}
      {mcsDoc?._generationError && !mcsDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(mcsDoc._generationError)}
        </div>
      )}

      {/* Some sections could not be generated */}
      {incompleteNotice && !showGenerating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {incompleteNotice}
        </div>
      )}

      {/* Stale generation warning */}
      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
          You can try generating again.
        </div>
      )}

      {/* Generation progress overlay */}
      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-700 mb-1">
            {generating
              ? 'Generating...'
              : 'Generation in progress in the background...'}
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div>

      {/* Sections tab */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          {Object.entries(grouped).map(([group, groupSectionsList]) => {
            const style = getGroupStyle(group);
            return (
              <div key={group} className={`border ${style.border} rounded-lg overflow-hidden`}>
                {/* Group header */}
                <div className={`${style.bg} px-4 py-2.5 flex items-center gap-2`}>
                  <BookOpen className={`h-4 w-4 ${style.text}`} />
                  <h3 className={`text-sm font-semibold ${style.text}`}>
                    {formatGroupLabel(group)}
                  </h3>
                  <span className="text-xs text-gray-400 ml-auto">
                    {groupSectionsList.length} section{groupSectionsList.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Sections in group */}
                <div className="divide-y divide-gray-100 bg-white">
                  {groupSectionsList.map((section) => {
                    const isExpanded = expandedSectionId === section.id;
                    const sectionPlaceholders = section.placeholderCount || section.placeholders?.length || 0;
                    const isRegenerating = regeneratingSection === section.id;
                    return (
                      <div key={section.id}>
                        {/* Section row */}
                        <div className="group">
                          <button
                            onClick={() =>
                              setExpandedSectionId(isExpanded ? null : section.id)
                            }
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="text-xs font-mono text-gray-400 w-10">
                              {section.number}
                            </span>
                            <span className="text-sm font-medium text-gray-800 flex-1">
                              {section.title}
                            </span>

                            {/* Placeholder count badge */}
                            {sectionPlaceholders > 0 && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                {sectionPlaceholders} placeholder{sectionPlaceholders !== 1 ? 's' : ''}
                              </span>
                            )}

                            {/* Section status badge */}
                            <SectionStatusBadge section={section} />

                            {/* Per-section regenerate icon: revealed on hover, always shown on a failed section (the notice points to it) */}
                            <span
                              className={regenerateIconClass(section)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRegenerateSection(section.sectionId || section.id);
                              }}
                              title="Regenerate this section"
                            >
                              {isRegenerating ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                              )}
                            </span>
                          </button>
                        </div>

                        <SectionFailureNotice section={section} regenerateHint={MCS_REGENERATE_HINT} />

                        {/* Expanded content */}
                        {isExpanded && section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <SafeHtml
                              className="prose prose-sm max-w-none text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[60vh] overflow-y-auto"
                              html={section.content}
                            />
                          </div>
                        )}
                        {isExpanded && !section.content && (
                          <div className="px-4 pb-4 pt-1 ml-11">
                            <p className="text-sm text-gray-400 italic">No content generated yet.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {sections.length === 0 && !showGenerating && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No sections found. Generate the Medical Coding Specification to create sections.
            </div>
          )}
        </div>
      )}

      {/* Coding Domains tab */}
      {activeTab === 'domains' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-gray-400" />
            <span className="text-xs text-gray-500">
              Coding domains detected from CRF forms and data dictionary fields
            </span>
            <span className="text-xs text-gray-400 ml-auto">
              {detectedDomains.length} domain{detectedDomains.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-44">
                      Domain
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">
                      Dictionary
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-24">
                      SDTM Domain
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">
                      Verbatim Variable
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">
                      SDTM target
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      CRF Forms
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-16">
                      Fields
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detectedDomains.map((domain, idx) => (
                    <tr key={`${domain.type}-${idx}`} className="hover:bg-gray-50 transition-colors align-top">
                      <td className="px-3 py-2 text-xs text-gray-700 font-medium">
                        {domain.displayName}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-medium">
                          {domain.dictionary}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600">
                        {domain.sdtmDomain}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600">
                        {domain.verbatimVariable || (
                          <span className="text-gray-400 italic font-sans" title="No verbatim term field is collected; structured metadata only.">
                            not collected
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600">
                        {domain.verbatimVariable ? domain.decodedVariable : ''}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 max-w-xs">
                        {domain.crfFormNames?.length > 0
                          ? domain.crfFormNames.join(', ')
                          : <span className="text-gray-400 italic">None detected</span>
                        }
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 text-center">
                        {domain.fieldCount}
                      </td>
                    </tr>
                  ))}
                  {detectedDomains.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                        No coding domains detected. Generate CRFs first, then generate the Medical Coding Specification.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      </div>{/* end scrollable content area */}

      {/* Regenerate option */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (confirm('Regenerate the entire Medical Coding Specification with the latest study data? Existing content will be overwritten.')) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate All Sections
        </button>
      </div>
    </div>
  );
}
